/**
 * Main parent file for the Grid/Snapshot tab
 * Contains:
 *  - the grid of images rendered from ImageGrid.js, 
 *  - the filter logic and features: 
 *    - SortingButton.js (by name/most recent update)
 *    - filtering by a given time period
 *    - StatusFilter.js (filter the current status of the vehicle: driving, parked, offline)
 *    - A search-by-name for all ravens in the current filter
 * - Checkbox for enabling/disabling inactivity
 * 
 * Rendered in sessionManager.js
 */

import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import RavenUtil from '../../common/ravenUtil';
import ProgressOverlay from '../../common/ProgressOverlay';
import Events from '../../common/events';

import ViewToggle from './ViewToggle';
import ImageGrid from './ImageGrid';
import SortingButton from './SortingButton';
import StatusFilter from './StatusFilter';
import TimeFilter from './TimeFilter';
import SearchByName from './SearchByName';

import './GridPage.scss';

import { setCamera } from '../dashboard/EventMedia/actions';
import { CameraType } from '../../../data/media';
import MainModuleGroup from '../../LayoutTemplateModularFullscreen/MainModuleGroup';
import { strings } from '../../../localization';
import { ACTIVE_MENU_ITEM_IDS, ACTIONS as layoutTemplateModularFullscreenDispatchActions } from '../../../data/layoutTemplateModularFullscreen/duck';

class GridPage extends PureComponent {

    state = {
        statusFilter: new Set(['driving', 'parked', 'offline']), // all are on by default
        nameSearch: '',
        sortBy: 'alphaNumero',
        timeFilter: 'all',
        dismissedRavens: new Set(),
        pauseInactivityEnabled: true,
        paused: false,
        showBanner: false,
    };

    componentDidMount() {
        if (!this.props.ravens) {
            this.props.getUserData(this.onUserDataLoaded);
        }
        else {
            this.onUserDataLoaded();
        }

        document.addEventListener(Events.PAUSE_DFAST, this.onPause);
        document.addEventListener(Events.UNPAUSE_DFAST, this.onUnpause);
    };

    componentDidUpdate(prevProps, prevState) {
        // once mqttService is available, disable inactivity timeout
        if (prevProps.mqttService !== this.props.mqttService && this.props.mqttService) {
            this.props.mqttService.enableInactivityTimeout();
            // setDfastInterval sets the mode and triggers sendAllRavensDfast internally
            this.props.mqttService.setDfastInterval();
            this.props.mqttService.updateActiveRavens();
        }
    }

    componentWillUnmount() {
        this.props.mqttService.enableInactivityTimeout();
        document.removeEventListener(Events.PAUSE_DFAST, this.onPause);
        document.removeEventListener(Events.UNPAUSE_DFAST, this.onUnpause);
    }

    onPause = () => {
        this.setState({ paused: true, showBanner: true });
    }

    onUnpause = () => {
        if (!this.state.showBanner) return; // nothing to dismiss
        this.setState({ paused: false });
        // Keep banner mounted during slide-up animation, then remove
        setTimeout(() => {
            this.setState({ showBanner: false });
        }, 800); // match animation duration, 0.8sec
    }

    onUserDataLoaded = () => {
        this.props.getCurrentVehicleStates();
    };

    toggleCamera = () => {
        let currentCamera = this.props.cameraType;
        this.props.toggleCamera(currentCamera);
    }

    handleStatusFilterChange = (status) => {
        this.setState(prevState => {
            const next = new Set(prevState.statusFilter);
            if (next.has(status)) {
                next.delete(status);
            } else {
                next.add(status);
            }
            return { statusFilter: next };
        });
    };

    handleNameSearchChange = (e) => {
        this.setState({
            nameSearch: e.target.value,
            currentPage: 1
        });
    };

    handleSortChange = (sortBy) => {
        this.setState({ sortBy });
    };

    handleTimeFilterChange = (e) => {
        this.setState({ timeFilter: e.target.value });
    };

    handleDismissRaven = (ravenUuid) => {
        this.setState(prevState => {
            const next = new Set(prevState.dismissedRavens);
            next.add(ravenUuid);
            return { dismissedRavens: next };
        });
    };

    handleResetDismissedRavens = () => {
        this.setState({ dismissedRavens: new Set() })
    }

    handlePauseOnInactivityChange = (e) => {
        const isChecked = e.target.checked;
        const { mqttService } = this.props;

        this.setState({ pauseInactivityEnabled: isChecked });

        if (mqttService) {
            if (isChecked) {
                // enable the timeout
                mqttService.enableInactivityTimeout();
            } else {
                // disable the timeout
                mqttService.disableInactivityTimeout();
            }
        }
    };

    getSortedRavens = () => {

        const { ravens } = this.props;
        const { sortBy, timeFilter, statusFilter, nameSearch, dismissedRavens } = this.state;

        if (!Array.isArray(ravens)) return [];

        const getRavenDate = (raven) => {
            if (raven.liveMedia?.[0]?.timestamp) {
                return new Date(raven.liveMedia[0].timestamp);
            }
            if (raven.vehicleState?.currentPreviewUrls?.[0]?.ts) {
                const ts = RavenUtil.convertTs(
                    raven.vehicleState.currentPreviewUrls[0].ts
                );
                return ts instanceof Date ? ts : new Date(ts);
            }
            return null;
        };

        let result = ravens.filter(r => !dismissedRavens.has(r.ravenUuid));

        // ---- status filter ----
        result = result.filter((raven) => {
            if (raven.vehicleState && (raven.vehicleState.ravenOffline || raven.unplugged)) {
                return statusFilter.has('offline');
            }
            if (!raven.isDriving || raven.isIdling) {
                return statusFilter.has('parked');
            }
            return statusFilter.has('driving');
        });

        // ---- name search ----
        if (nameSearch.trim()) {
            const search = nameSearch.trim().toLowerCase();
            result = result.filter(
                (r) => r.vehicle?.name?.toLowerCase().includes(search)
            );
        }

        // ---- time filter ----
        if (timeFilter !== 'all') {
            const now = Date.now();
            const limits = {
                '30d': 30 * 24 * 60 * 60 * 1000,
                '7d': 7 * 24 * 60 * 60 * 1000,
                '48h': 48 * 60 * 60 * 1000,
                '24h': 24 * 60 * 60 * 1000,
                '1h': 60 * 60 * 1000
            };

            const windowMs = limits[timeFilter];
            if (windowMs) {
                result = result.filter((r) => {
                    const d = getRavenDate(r);
                    return d && now - d.getTime() <= windowMs;
                });
            }
        }

        // ---- sorting ----
        switch (sortBy) {
            case 'time':
                result.sort(
                    (a, b) =>
                        (getRavenDate(b)?.getTime() ?? 0) -
                        (getRavenDate(a)?.getTime() ?? 0)
                );
                break;
            case 'alphaNumero':
            default:
                result.sort((a, b) =>
                    a.vehicle.name.localeCompare(b.vehicle.name, undefined, { numeric: true })
                );
                break;
        }

        return result;
    };

    onOptionsClick = () => {
        this.props.setMainModuleGroupNavigation(false); // isCollapsed false
    }

    render() {

        if (!this.props.ravens) {
            return (
                <div className="grid-page">
                    <ProgressOverlay />
                </div>
            );
        }

        const ravensToPass = this.getSortedRavens();
        const noRavensMatchFilter =
            Array.isArray(this.props.ravens) &&
            this.props.ravens.length > 0 &&
            ravensToPass.length === 0;


        return (
            <MainModuleGroup
                mainModuleGroupNavigation={ //side panel
                    <div className='section-divs grid-page-options-panel'>
                        <SearchByName
                            type="text"
                            placeholder={strings.search_by_name}
                            value={this.state.nameSearch}
                            onChange={this.handleNameSearchChange}
                            className="raven-search-input"
                        />

                        <SortingButton
                            value={this.state.sortBy}
                            onChange={this.handleSortChange}
                        />

                        <TimeFilter
                            value={this.state.timeFilter}
                            onChange={this.handleTimeFilterChange}
                        />

                        {/* Pause updates on inactivity checkbox */}
                        {/* hiding for now (RAV-8638), but there may be changes when we move user preferences from local storage to cloud
                            {!this.state.pauseInactivityEnabled &&
                            <div className="pause-inactivity-wrapper">
                                <input
                                    type="checkbox"
                                    id="pause-on-inactivity"
                                    className="pause-inactivity-checkbox"
                                    checked={this.state.pauseInactivityEnabled}
                                    onChange={this.handlePauseOnInactivityChange}
                                />
                                <label htmlFor="pause-on-inactivity" className="pause-inactivity-label">
                                    {strings.pause_automatic_updates}
                                </label>
                            </div>
                        */}

                        <div></div>
                        <div>
                            <StatusFilter
                                statusFilter={this.state.statusFilter}
                                onToggle={this.handleStatusFilterChange}
                            />
                        </div>

                        <div></div>
                        <button
                            className="btn reset-dismissed-btn"
                            onClick={this.handleResetDismissedRavens}
                        >
                            {strings.reset_dismissed_ravens}
                        </button>

                    </div>
                }

                mainModuleGroup={ //main
                    <div className='container grid-page'>
                        {this.state.showBanner &&
                            <div className={`grid-paused-banner ${this.state.paused ? 'slide-down' : 'slide-up'}`}>
                                {strings.pausescreen_m1}{strings.pausescreen_m2}
                            </div>
                        }
                        <div className="grid-page-header">
                            <div className="grid-header-col">
                                <div className="header-title-wrapper">
                                    <h1 className="d-inline-block">{strings.grid_view_title}</h1>

                                </div>
                                <p className="review-report-description">
                                    {
                                        <> {strings.previews_update}</>
                                    }
                                </p>
                            </div>
                            <div className="review-header-col options">
                                <div className="filter-col">
                                    <button className="btn" onClick={this.onOptionsClick}>{strings.event_review_all_filter_options}</button>
                                </div>
                            </div>
                        </div>
                        <div className='snapshot-grid-sizer'>
                            <div className="live-image-wrapper">
                                {noRavensMatchFilter ?
                                    <div>
                                        <h2 className="no-filter-results">
                                            {strings.no_filter_results}
                                        </h2>
                                        <h4 className="no-filter-results">
                                            {strings.no_ravens_match}
                                        </h4>
                                    </div>
                                    :
                                    <ImageGrid
                                        {...this.props}
                                        ravens={ravensToPass}
                                        onDismissRaven={this.handleDismissRaven}
                                        toggleCamera={this.toggleCamera}
                                        camera={this.props.cameraType}
                                    />
                                }
                            </div>
                        </div>
                    </div>
                }
            />
        );
    }
}

GridPage.propTypes = {
    cameraType: PropTypes.string.isRequired,
    toggleCamera: PropTypes.func.isRequired,
}

const mapStateToProps = (state) => {
    return {
        cameraType: state.scenes.dashboard.EventMedia.cameraType,
    };
}

const mapDispatchToProps = (dispatch, ownProps) => {
    return {
        toggleCamera: (currentCamera) => {
            dispatch(
                setCamera(
                    currentCamera === CameraType.CAMERA_ROAD
                        ? CameraType.CAMERA_CABIN
                        : CameraType.CAMERA_ROAD
                )
            );
        },
        setMainModuleGroupNavigation: (isCollapsed) => dispatch(layoutTemplateModularFullscreenDispatchActions.setMainModuleGroupNavigation(isCollapsed)),

    }
}

export default connect(mapStateToProps, mapDispatchToProps)(GridPage);